import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useAuth } from '@/auth/AuthProvider'
import { ALLOW_SIGNUP } from '@/auth/supabase'
import { Button, Field, Input } from '@/components/ui/controls'
import { Spinner } from '@/components/ui/feedback'
import { AuthShell } from './login'

export const Route = createFileRoute('/signup')({
  // Hiding the link is not enough; the route has to refuse too, or /signup is
  // still reachable by typing it.
  beforeLoad: () => {
    if (!ALLOW_SIGNUP) throw redirect({ to: '/login' })
  },
  component: SignupPage,
})

function SignupPage() {
  const { signUp } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await signUp(email, password)
      // A new account has no subscription, so /upgrade is the honest landing
      // place — not the portal, which would 403 on its first request.
      navigate({ to: '/upgrade', replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create account')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell
      title="Create account"
      footer={
        <>
          Already have one?{' '}
          <Link to="/login" className="text-accent hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Field label="Email" htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </Field>
        <Field label="Password" htmlFor="password">
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <p className="text-xs text-subtle">At least 8 characters.</p>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" className="w-full" disabled={busy}>
          {busy && <Spinner />}
          Create account
        </Button>
      </form>
    </AuthShell>
  )
}
